import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../lib/auth";
import { setup2FA, verify2FASetup, disable2FA } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../components/ui/input-otp";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  ShieldCheck,
  ShieldOff,
  QrCode,
  Key,
  ArrowLeft,
  AlertCircle,
} from "lucide-react";

export default function Setup2FA() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [otpValue, setOtpValue] = useState("");
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableOtp, setDisableOtp] = useState("");

  const handleSetup = async () => {
    setLoading(true);
    try {
      const response = await setup2FA();
      setSetupData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to setup 2FA");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (otpValue.length !== 6) {
      toast.error("Enter complete 6-digit code");
      return;
    }

    setLoading(true);
    try {
      await verify2FASetup(otpValue);
      toast.success("2FA enabled successfully!");
      updateUser({ totp_enabled: true });
      navigate("/profile");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
      setOtpValue("");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (disableOtp.length !== 6) {
      toast.error("Enter complete 6-digit code");
      return;
    }

    setLoading(true);
    try {
      await disable2FA(disableOtp);
      toast.success("2FA disabled");
      updateUser({ totp_enabled: false });
      setShowDisableModal(false);
      setDisableOtp("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to disable 2FA");
      setDisableOtp("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate("/profile")}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Profile
          </button>
          <h1 className="text-3xl font-heading font-bold mb-2">
            Two-Factor Authentication
          </h1>
          <p className="text-muted-foreground">
            Add an extra layer of security with Google Authenticator
          </p>
        </motion.div>

        {user?.totp_enabled ? (
          /* 2FA Enabled State */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-2xl p-8 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6 neon-glow">
              <ShieldCheck className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-2xl font-heading font-bold mb-2">2FA is Enabled</h2>
            <p className="text-muted-foreground mb-8">
              Your account is protected with Google Authenticator
            </p>

            <Button
              variant="outline"
              onClick={() => setShowDisableModal(true)}
              className="border-destructive/50 text-destructive hover:bg-destructive/10"
              data-testid="disable-2fa-btn"
            >
              <ShieldOff className="w-5 h-5 mr-2" /> Disable 2FA
            </Button>
          </motion.div>
        ) : setupData ? (
          /* Setup in Progress */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-2xl p-8"
          >
            <div className="text-center mb-8">
              <h2 className="text-xl font-heading font-bold mb-2">
                Scan QR Code
              </h2>
              <p className="text-muted-foreground">
                Open Google Authenticator and scan this code
              </p>
            </div>

            {/* QR Code */}
            <div className="flex justify-center mb-8">
              <div className="p-4 bg-white rounded-xl">
                <QRCodeSVG value={setupData.qr_uri} size={200} />
              </div>
            </div>

            {/* Manual Entry */}
            <div className="mb-8">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Key className="w-4 h-4" /> Manual entry key:
              </div>
              <div className="p-4 bg-background/50 rounded-lg border border-white/10 font-mono text-center break-all">
                {setupData.secret}
              </div>
            </div>

            {/* Verification */}
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Enter the 6-digit code from your app:
              </p>
              <div className="flex justify-center mb-6">
                <InputOTP
                  maxLength={6}
                  value={otpValue}
                  onChange={setOtpValue}
                  data-testid="setup-otp-input"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="border-primary/30" />
                    <InputOTPSlot index={1} className="border-primary/30" />
                    <InputOTPSlot index={2} className="border-primary/30" />
                    <InputOTPSlot index={3} className="border-primary/30" />
                    <InputOTPSlot index={4} className="border-primary/30" />
                    <InputOTPSlot index={5} className="border-primary/30" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                onClick={handleVerify}
                disabled={loading || otpValue.length !== 6}
                className="neon-glow font-heading font-bold uppercase"
                data-testid="verify-2fa-setup-btn"
              >
                {loading ? "Verifying..." : "Enable 2FA"}
              </Button>
            </div>
          </motion.div>
        ) : (
          /* Initial State */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-2xl p-8 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
              <QrCode className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-heading font-bold mb-2">
              Protect Your Account
            </h2>
            <p className="text-muted-foreground mb-6">
              Two-factor authentication adds an extra layer of security by
              requiring a code from your phone when you sign in.
            </p>

            <div className="bg-muted/50 rounded-lg p-4 mb-8 text-left">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-primary mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium mb-1">Before you begin:</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• Download Google Authenticator on your phone</li>
                    <li>• Keep your backup codes safe</li>
                  </ul>
                </div>
              </div>
            </div>

            <Button
              onClick={handleSetup}
              disabled={loading}
              className="neon-glow font-heading font-bold uppercase"
              data-testid="start-2fa-setup-btn"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  Setting up...
                </div>
              ) : (
                <>
                  <ShieldCheck className="w-5 h-5 mr-2" /> Set Up 2FA
                </>
              )}
            </Button>
          </motion.div>
        )}

        {/* Disable Modal */}
        <Dialog open={showDisableModal} onOpenChange={setShowDisableModal}>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle className="font-heading">Disable 2FA</DialogTitle>
              <DialogDescription>
                Enter your authenticator code to disable two-factor authentication.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="flex justify-center mb-6">
                <InputOTP
                  maxLength={6}
                  value={disableOtp}
                  onChange={setDisableOtp}
                  data-testid="disable-otp-input"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="border-destructive/30" />
                    <InputOTPSlot index={1} className="border-destructive/30" />
                    <InputOTPSlot index={2} className="border-destructive/30" />
                    <InputOTPSlot index={3} className="border-destructive/30" />
                    <InputOTPSlot index={4} className="border-destructive/30" />
                    <InputOTPSlot index={5} className="border-destructive/30" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                onClick={handleDisable}
                disabled={loading || disableOtp.length !== 6}
                variant="destructive"
                className="w-full"
                data-testid="confirm-disable-2fa-btn"
              >
                {loading ? "Disabling..." : "Disable 2FA"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
